import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'

export interface ImprovementIdeaVisualizationProps {
  /**
   * Title for the improvement idea section
   */
  title: string
  /**
   * Description text explaining the improvement suggestion
   */
  description: string
  /**
   * Recommendation text to display in glass label
   */
  recommendation: string
  /**
   * Variant for team or individual styling
   */
  variant?: 'team' | 'individual'
}

/**
 * ImprovementIdeaVisualization - Displays improvement suggestion with glass label
 *
 * Features:
 * - Radial gradient background with glassmorphism
 * - Title and description text
 * - Large glass effect label pill (278px x 87px)
 * - Used for displaying improvement recommendations to gamers
 */
export const ImprovementIdeaVisualization: React.FC<ImprovementIdeaVisualizationProps> = ({
  title,
  description,
  recommendation,
  variant: _variant // Reserved for future background color variations
}) => {
  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        gap: '16px',
        isolation: 'isolate',
        position: 'relative',
        width: '100%',
        height: '100%',
        background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
        backgroundBlendMode: 'soft-light, normal',
        border: `1px solid ${INTERAX_COLORS.white04}`,
        boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        flex: 'none',
        order: 0,
        flexGrow: 0,
        overflow: 'hidden'
      }}
    >
      {/* Orange glow ellipse - bottom center */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '177px',
          left: '50%',
          bottom: '-120px',
          transform: 'translateX(-50%)',
          background: INTERAX_COLORS.sun,
          filter: 'blur(75px)',
          zIndex: 0
        }}
      />

      {/* Title */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.headline5,
          color: INTERAX_COLORS.white,
          alignSelf: 'stretch',
          textAlign: 'center',
          zIndex: 1
        }}
      >
        {title}
      </Typography>

      {/* Description Text */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.body3,
          color: INTERAX_COLORS.thunderGrey,
          alignSelf: 'stretch',
          flex: 1,
          textAlign: 'left',
          zIndex: 1
        }}
      >
        {description}
      </Typography>

      {/* Glass Effect Label - Frame 7 */}
      {recommendation && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '6px 16px',
            gap: '10px',
            width: '100%',
            minHeight: '64px',
            background: 'rgba(255, 255, 255, 0.1)',
            //boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.25), inset 0px -1px 1px #D1D7E3',
            borderRadius: '8px',
            flex: 'none',
            order: 2,
            flexGrow: 0,
            zIndex: 2,
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              textAlign: 'center',
              color: INTERAX_COLORS.white
            }}
          >
            {recommendation}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ImprovementIdeaVisualization
